import { useLayoutEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Grid, TablePagination } from "@mui/material"

import type { AlertSettingAccordion, AlertsFilterOptions } from "types/alerts.types"
import {
  selectAlertAccordions,
  selectEntityAlertAccordions,
  setAlertAccordionsRowsAmount,
  setEntityAlertAccordionsRowsAmount,
} from "features/store/tablesSlice"
import { paginateList } from "helpers/utils/common"
import { ROWS_PER_PAGE_OPTIONS } from "helpers/utils/constants"
import AlertAccordion from "widgets/alerts/AlertAccordion"
import Message from "widgets/common/Message"

interface AccordionsSectionProps {
  alerts: AlertSettingAccordion[]
  filters: AlertsFilterOptions | null
  // A device, power line or tower page, where every alert has the same entity and fewer
  // of them show per page.
  entityPage?: boolean
}

const AccordionsSection = ({ alerts, filters, entityPage }: AccordionsSectionProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const selectAccordions = entityPage
    ? selectEntityAlertAccordions
    : selectAlertAccordions
  const setRowsAmount = entityPage
    ? setEntityAlertAccordionsRowsAmount
    : setAlertAccordionsRowsAmount
  const { qtyRows } = useSelector(selectAccordions)
  const [page, setPage] = useState(0)
  const [pageFilters, setPageFilters] = useState(filters)
  // A filter change starts over from the first page.
  if (filters !== pageFilters) {
    setPageFilters(filters)
    setPage(0)
  }
  // Moves to the last page left when the list shrinks, for example after a delete, and
  // stays there if the list grows back.
  const lastPage = Math.max(Math.ceil(alerts.length / qtyRows) - 1, 0)
  if (page > lastPage) {
    setPage(lastPage)
  }
  const pagedAlerts = useMemo(
    () => paginateList(page, alerts, qtyRows),
    [page, alerts, qtyRows],
  )

  const listRef = useRef<HTMLDivElement>(null)
  // Paging from the footer brings the list's top back on screen, as the tables do. It waits
  // for the new page's alerts, so the old ones never move up first.
  const isScrollPending = useRef(false)
  useLayoutEffect(() => {
    if (!isScrollPending.current) {
      return
    }
    isScrollPending.current = false
    const list = listRef.current
    if (list && list.getBoundingClientRect().top < 0) {
      list.scrollIntoView({ block: "start" })
    }
  }, [page])

  return (
    <>
      <Grid ref={listRef} container spacing={2} mb={2}>
        {pagedAlerts.length ? (
          pagedAlerts.map((alert) => {
            return (
              <Grid key={alert.id} item xs={12}>
                <AlertAccordion alert={alert} hideEntity={entityPage} />
              </Grid>
            )
          })
        ) : (
          <Message messageKey="alerts.NO_ALERTS_ACCORDIONS" />
        )}
      </Grid>
      {alerts.length > 0 && (
        <TablePagination
          component="div"
          count={alerts.length}
          page={page}
          onPageChange={(_, newPage) => {
            isScrollPending.current = true
            setPage(newPage)
          }}
          rowsPerPage={qtyRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={(event) => {
            dispatch(setRowsAmount(Number(event.target.value)))
            setPage(0)
          }}
          labelRowsPerPage={`${t("table.ROWS_PER_PAGE")}:`}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} ${t("table.OF")} ${count}`
          }
          getItemAriaLabel={(type) =>
            t(type === "previous" ? "table.PREVIOUS_PAGE" : "table.NEXT_PAGE")
          }
          // On a phone the picker is hidden so the arrows fit, as in the tables' footer.
          sx={{
            "& .MuiTablePagination-selectLabel": { display: { xs: "none", sm: "block" } },
            "& .MuiTablePagination-input": { display: { xs: "none", sm: "inline-flex" } },
          }}
        />
      )}
    </>
  )
}

export default AccordionsSection
